import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { toast } from 'react-toastify';
import { EditLocationDialogProps } from '../../utils/DataTypes';

const EditLocationDialog: React.FC<EditLocationDialogProps> = ({
  isOpen,
  formValues,
  originalValues,
  setFormValues,
  onClose,
  onSave,
}) => {
  const dataProvider = useDataProvider();
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) setErrorMessage(null);
  }, [isOpen, formValues.location_number]);

  const handleRestore = () => {
    setFormValues(originalValues);
    setErrorMessage(null);
    toast.info('Changes discarded. Values restored to original.');
  };

  const handleValidationAndSave = async () => {
    setErrorMessage(null);

    const cleanNumber = formValues.location_number?.trim() || '';
    const cleanName = formValues.location_name?.trim() || '';

    if (!cleanNumber || !cleanName) {
      setErrorMessage('Location Number and Name are required.');
      return;
    }

    if (cleanNumber === originalValues.location_number.trim()) {
      await onSave();
      return;
    }

    setIsValidating(true);
    try {
      const { data: duplicates } = await dataProvider.getList('ValidateLocations', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: formValues.billing_client_number,
          location_numbers: [cleanNumber],
        },
      });

      if (duplicates.length > 0) {
        setErrorMessage(`Location ${cleanNumber} is already used for this client.`);
        setIsValidating(false);
        return;
      }

      await onSave();
    } catch (err) {
      toast.error('Validation failed.');
    } finally {
      setIsValidating(false);
    }
  };

  const hasChanges = JSON.stringify(formValues) !== JSON.stringify(originalValues);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Location Crosswalk</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box display="flex" gap={2} alignItems="center" mb={3}>
            <TextField label="Client Name" value={formValues.billing_client_name} fullWidth disabled />
            <TextField
              label="Client Number"
              value={formValues.billing_client_number || ''}
              disabled
              sx={{ width: '200px' }}
            />
          </Box>

          <TextField
            label="Location Number"
            value={formValues.location_number}
            error={!!errorMessage && !formValues.location_number?.trim()}
            onChange={(e) => {
              const val = e.target.value;
              setFormValues((prev: any) => ({
                ...prev,
                location_number: val,
                location_display_number:
                  prev.location_display_number === prev.location_number ? val : prev.location_display_number,
              }));
            }}
            fullWidth
            required
          />
          <TextField
            label="Display Number"
            value={formValues.location_display_number}
            onChange={(e) => setFormValues((prev: any) => ({ ...prev, location_display_number: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Location Name"
            value={formValues.location_name}
            error={!!errorMessage && !formValues.location_name?.trim()}
            onChange={(e) => setFormValues((prev: any) => ({ ...prev, location_name: e.target.value }))}
            fullWidth
            required
          />

          {errorMessage && (
            <Box p={1.5} sx={{ backgroundColor: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 1 }}>
              <Typography color="error" variant="body2">
                ⚠️ {errorMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleRestore} color="inherit" disabled={!hasChanges || isValidating}>
          Restore Original
        </Button>
        <Box>
          <Button onClick={onClose} disabled={isValidating} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleValidationAndSave}
            color="primary"
            variant="contained"
            disabled={!hasChanges || isValidating}
          >
            {isValidating ? 'Validating...' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditLocationDialog;
